import {Alert, Icon} from '@hm/fundamental'
import {mdiClose, mdiRefresh} from '@mdi/js'
import {Box, CircularProgress, IconButton, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from 'tss-react/mui'

import {OrderChangeBannerVariant} from '../../types/orderChangeBanner.types'

interface OrderChangeBannerProps {
  /** The variant of the banner to display */
  variant: OrderChangeBannerVariant
  /** Callback when dismiss button is clicked */
  onDismiss?: () => void
  /** Callback when refresh button is clicked */
  onRefresh?: () => void
  /** Whether the banner can be dismissed */
  isDismissible?: boolean
  /** Whether dismiss is in progress */
  isDismissing?: boolean
  /** Whether a refresh is in progress */
  isRefreshing?: boolean
}

const ALERT_VARIANTS: Record<OrderChangeBannerVariant, 'info' | 'warning' | 'error'> = {
  changeRequestProcessing: 'info',
  changeRequestFailed: 'error',
  manualApprovalPending: 'warning',
  changesApplied: 'info'
}

const useStyles = makeStyles()(() => ({
  alert: {'& > div:last-child': {flex: 1}},
  textContainer: {flex: 1},
  title: {fontWeight: 600, fontSize: 14, lineHeight: '20px', marginBottom: 4},
  description: {fontSize: 14, lineHeight: '20px'}
}))

export const OrderChangeBanner: React.FC<OrderChangeBannerProps> = ({
  variant,
  onDismiss,
  onRefresh,
  isDismissible = false,
  isDismissing = false,
  isRefreshing = false
}) => {
  const {t} = useTranslation()
  const {classes} = useStyles()

  const alertVariant = ALERT_VARIANTS[variant]

  const getContent = (): {title?: string; description: string} => {
    switch (variant) {
      case 'changeRequestProcessing':
        return {
          description: t(
            'orderIntake.rmc.orderManagement.banner.changeRequestProcessing',
            'This order is being updated, please check back in few minutes to see requested changes.'
          )
        }
      case 'changeRequestFailed':
        return {
          title: t(
            'orderIntake.rmc.orderManagement.banner.changeRequestFailed.title',
            'Change request for this order was not approved.'
          ),
          description: t(
            'orderIntake.rmc.orderManagement.banner.changeRequestFailed.description',
            'If you have any doubts regarding this change request, please contact dispatch.'
          )
        }
      case 'manualApprovalPending':
        return {
          title: t(
            'orderIntake.rmc.orderManagement.banner.manualApprovalPending.title',
            "A change was requested for this order and we're reviewing it."
          ),
          description: t(
            'orderIntake.rmc.orderManagement.banner.manualApprovalPending.description',
            'Please check back later to see the status of this request.'
          )
        }
      case 'changesApplied':
        return {
          title: t(
            'orderIntake.rmc.orderManagement.banner.changesApplied.title',
            'Changes have been applied to this order.'
          ),
          description: t(
            'orderIntake.rmc.orderManagement.banner.changesApplied.description',
            'Click refresh to see the updated order details.'
          )
        }
      default:
        return {description: ''}
    }
  }

  const content = getContent()
  const showDismissButton = isDismissible && onDismiss

  return (
    <Alert
      variant={alertVariant}
      data-test-id={`order-change-banner-${variant}`}
      className={classes.alert}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%'
        }}
      >
        <Box className={classes.textContainer}>
          {content.title && <Typography className={classes.title}>{content.title}</Typography>}
          <Typography className={classes.description}>{content.description}</Typography>
        </Box>
        {variant === 'changesApplied' && onRefresh && (
          <IconButton
            onClick={onRefresh}
            disabled={isRefreshing}
            size="small"
            aria-label="Refresh order data"
            data-test-id="order-change-banner-refresh"
            sx={{padding: '4px', flexShrink: 0}}
          >
            {isRefreshing ? (
              <CircularProgress size={20} />
            ) : (
              <Icon path={mdiRefresh} size="regular" />
            )}
          </IconButton>
        )}
        {showDismissButton && (
          <IconButton
            onClick={onDismiss}
            disabled={isDismissing}
            size="small"
            aria-label="Dismiss banner"
            data-test-id="order-change-banner-dismiss"
            sx={{padding: '4px', flexShrink: 0}}
          >
            <Icon path={mdiClose} size="regular" />
          </IconButton>
        )}
      </Box>
    </Alert>
  )
}
